import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MoreHorizontal, CalendarDays, ScrollText, MessageSquare, CalendarClock,
  Plus, User as UserIcon, Archive,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, initialsOf } from "@/lib/utils";
import { HEALTH, PLACEMENT_STATUS, fmtDateFr, relAgo, monthsLeft } from "./placementsShared";
import SuiviTab from "./SuiviTab";
import { ContractTab, RelationTab, DocumentsTab, ActivityTab } from "./ContextTabs";
import { OverviewTab, PerformanceTab, IntegrationCard, AddInteractionDialog } from "./WorkspaceTabs";
import { AddFollowUpDialog, ClosePlacementDialog } from "./FollowUpDialogs";
import { useLang, translateFr as tfr } from "@/i18n";

function ContextCard({ Icon, label, main, sub, testid }) {
  return (
    <div className="min-w-0 rounded-xl border border-border dark:border-border bg-card p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
        <Icon className="h-3 w-3 shrink-0" /> <span className="truncate">{label}</span>
      </div>
      <div className="text-sm font-semibold mt-1.5 truncate">{main || "—"}</div>
      <div className="text-xs text-muted-foreground mt-0.5 truncate">{sub || "\u00A0"}</div>
    </div>
  );
}

const DETAIL_TABS = [
  { v: "vue", l: tfr("Vue d'ensemble") }, { v: "performance", l: "Performance" }, { v: "contrat", l: tfr("Contrat") },
  { v: "integration", l: tfr("Intégration") }, { v: "relation", l: tfr("Relation") }, { v: "documents", l: "Documents" },
  { v: "etapes", l: tfr("Prochaines étapes") }, { v: "activite", l: tfr("Activité") },
];

export default function PlacementDetail({ detail, onRefresh }) {
  const { tf } = useLang();
  const navigate = useNavigate();
  const [addFu, setAddFu] = useState(false);
  const [closing, setClosing] = useState(false);
  const [addInteraction, setAddInteraction] = useState(false);
  const [tabVal, setTabVal] = useState("vue");
  const a = detail.athlete || {};
  const h = HEALTH[detail.health?.state] || HEALTH.NORMAL;
  const isActive = detail.status === "ACTIVE";
  const nextFu = detail.next_followup;
  const endDate = detail.contract_end_date;
  const ml = monthsLeft(endDate);
  const li = a.last_interaction_at;

  return (
    <div className="min-w-0" data-testid="placement-detail">
      {/* Header */}
      <div className="flex items-start gap-3.5">
        <Avatar className="h-14 w-14 shrink-0">
          <AvatarImage src={a.photo_url || undefined} alt="" />
          <AvatarFallback className="text-base font-semibold bg-secondary">{initialsOf(a.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="text-xl font-bold tracking-tight truncate" data-testid="placement-athlete-name">{a.name}</h2>
            <span className={cn("text-[10px] font-medium rounded-full border px-2 py-0.5",
              isActive ? h.chip : "bg-secondary text-muted-foreground border-border")}
              data-testid="placement-health-badge">
              {isActive ? tf(h.label) : tf(PLACEMENT_STATUS[detail.status] || detail.status)}
            </span>
          </div>
          <div className="text-sm text-muted-foreground truncate mt-0.5">
            {[detail.club || a.club, a.position].filter(Boolean).join(" · ") || "—"}
          </div>
          <div className="text-xs text-muted-foreground/80 mt-0.5">
            {[a.age ? `${a.age} ${tfr("ans")}` : null, a.level].filter(Boolean).join(" · ")}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-full p-2 hover:bg-accent dark:hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="placement-menu" aria-label={tf("Actions du placement")}>
              <MoreHorizontal className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            {isActive && (
              <DropdownMenuItem onClick={() => setAddFu(true)} data-testid="menu-add-followup">
                <Plus className="h-4 w-4 mr-2" /> {tf("Ajouter un suivi")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate(`/players/${a.id}`)} data-testid="menu-open-athlete">
              <UserIcon className="h-4 w-4 mr-2" /> {tf("Ouvrir l'athlète")}
            </DropdownMenuItem>
            {detail.contract?.id && (
              <DropdownMenuItem onClick={() => navigate(`/contracts/${detail.contract.id}`)} data-testid="menu-open-contract">
                <ScrollText className="h-4 w-4 mr-2" /> {tf("Ouvrir le contrat")}
              </DropdownMenuItem>
            )}
            {isActive && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setClosing(true)} className="text-athlynx focus:text-athlynx" data-testid="menu-close-placement">
                  <Archive className="h-4 w-4 mr-2" /> {tf("Clôturer le placement")}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Context cards */}
      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 xl:grid-cols-4 gap-2.5 mt-4" data-testid="placement-context-cards">
        <ContextCard Icon={CalendarDays} label={tf("Date du placement")}
          main={detail.placement_date ? fmtDateFr(detail.placement_date) : tf("À compléter")}
          sub={detail.placement_date ? relAgo(detail.placement_date) : null} testid="ctx-placement-date" />
        <ContextCard Icon={ScrollText} label={tf("Contrat jusqu'au")}
          main={endDate ? fmtDateFr(endDate) : tf("Non renseigné")}
          sub={ml != null ? `${ml} ${tf("mois restants")}` : null} testid="ctx-contract-end" />
        <ContextCard Icon={MessageSquare} label={tf("Dernier échange")}
          main={li ? relAgo(li) : tf("Aucun")}
          sub={li ? fmtDateFr(li) : null} testid="ctx-last-interaction" />
        <ContextCard Icon={CalendarClock} label={tf("Prochain suivi")}
          main={nextFu ? (nextFu.days != null && nextFu.days < 0 ? `${tf("En retard de")} ${Math.abs(nextFu.days)} ${tf("j")}` : relAgo(nextFu.scheduled_at)) : tf("Aucun")}
          sub={nextFu ? fmtDateFr(nextFu.scheduled_at) : null} testid="ctx-next-followup" />
      </div>

      {/* Tabs — compact section selector on mobile, wrapping tab bar from sm up */}
      <div className="sm:hidden mt-5">
        <Select value={tabVal} onValueChange={setTabVal}>
          <SelectTrigger className="h-10 rounded-xl border-border bg-card text-sm font-medium" data-testid="placement-tab-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DETAIL_TABS.map((t) => <SelectItem key={t.v} value={t.v}>{tf(t.l)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Tabs value={tabVal} onValueChange={setTabVal} className="mt-1 sm:mt-5">
        <TabsList className="hidden sm:flex flex-wrap bg-transparent border-b border-border dark:border-border rounded-none w-full justify-start h-auto p-0 gap-0">
          {DETAIL_TABS.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`placement-tab-${t.v}`}
              className="flex-none rounded-none -mb-px border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:bg-transparent text-muted-foreground px-3 md:px-4 py-2.5 text-xs md:text-sm whitespace-nowrap">
              {tf(t.l)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="vue" className="mt-4"><OverviewTab detail={detail} onRefresh={onRefresh} /></TabsContent>
        <TabsContent value="performance" className="mt-4"><PerformanceTab detail={detail} /></TabsContent>
        <TabsContent value="contrat" className="mt-4"><ContractTab detail={detail} /></TabsContent>
        <TabsContent value="integration" className="mt-4"><IntegrationCard detail={detail} full onRefresh={onRefresh} /></TabsContent>
        <TabsContent value="relation" className="mt-4"><RelationTab detail={detail} /></TabsContent>
        <TabsContent value="documents" className="mt-4"><DocumentsTab detail={detail} /></TabsContent>
        <TabsContent value="etapes" className="mt-4"><SuiviTab detail={detail} onRefresh={onRefresh} /></TabsContent>
        <TabsContent value="activite" className="mt-4"><ActivityTab detail={detail} onAddInteraction={() => setAddInteraction(true)} /></TabsContent>
      </Tabs>

      <AddInteractionDialog open={addInteraction} onOpenChange={setAddInteraction} playerId={a.id} onSaved={onRefresh} />

      <AddFollowUpDialog open={addFu} onOpenChange={setAddFu} placementId={detail.id} onSaved={onRefresh} />
      <ClosePlacementDialog open={closing} onOpenChange={setClosing} placementId={detail.id} onClosed={onRefresh} />
    </div>
  );
}
